import React, { useCallback } from 'react';
import { tA11y } from '@lwtears/lwt-common-frontend/lib/@common/util';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { Box, DialogActions, DialogContent, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ButtonWithFontAwesome from '../Buttons/ButtonWithFontAwesome';
import { NavigationDirection } from '../../../types';

type ActivityProps = {
  title: string;
  subtitle: string;
  content: string;
  children?: React.ReactNode;
  onClose: () => void;
  onContinue: () => void;
  continueButtonLabel: string;
  closeButtonLabel: string;
  direction: NavigationDirection;
  handleAfterContinue?: () => void;
};

const ActivityModal = (props: ActivityProps) => {
  const {
    subtitle,
    content,
    children,
    onClose,
    onContinue,
    continueButtonLabel,
    closeButtonLabel,
    direction,
    handleAfterContinue,
  } = props;
  const [, { closeModal }] = useModal();
  const classes = useStyles();

  const handleClose = useCallback(() => {
    onClose?.();
    closeModal();
  }, [onClose]);

  const handleContinue = useCallback(() => {
    onContinue?.();
    closeModal();
    handleAfterContinue?.();
  }, [onContinue]);

  const directionText =
    direction === NavigationDirection.Next
      ? tA11y('activitiesModal.next')
      : tA11y('activitiesModal.previous');

  return (
    <DialogContent className={classes.content}>
      <Box className={classes.subtitle}>
        <Typography variant="h2">{tA11y(`activitiesModal.${subtitle}`, { direction })}</Typography>
      </Box>
      <Box className={classes.textContainer}>
        {content && <Typography paragraph>{tA11y(`activitiesModal.${content}`)}</Typography>}
      </Box>
      <Box className={classes.componentContainer}>{children}</Box>
      <DialogActions className={classes.footer}>
        <ButtonWithFontAwesome
          onClick={handleClose}
          text={tA11y(`activitiesModal.${closeButtonLabel}`)}
          aria-label={tA11y(`activitiesModal.${closeButtonLabel}`)}
          color="primary"
          variant="outlined"
        />
        <ButtonWithFontAwesome
          onClick={handleContinue}
          text={tA11y(`activitiesModal.${continueButtonLabel}`, {
            directionText,
          })}
          aria-label={tA11y(`activitiesModal.${continueButtonLabel}`, {
            directionText,
          })}
          className={classes.containedButton}
          color="primary"
        />
      </DialogActions>
    </DialogContent>
  );
};
export default ActivityModal;

const useStyles = makeStyles((theme) => ({
  componentContainer: {},
  content: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '600px',
    height: 'auto',
    flex: 1,
    overflow: 'hidden',
    padding: '16px 24px',
  },
  footer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  },
  subtitle: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-start',
  },
  textContainer: {
    marginTop: 12,
  },
  containedButton: {
    backgroundColor: `${theme.palette.primary.main}`,
    color: `${theme.palette.primary.contrastText}`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.dark}`,
      color: `${theme.palette.primary.contrastText}`,
    },
    '&:disabled': {
      color: `${theme.palette.primary.contrastText}`,
    },
  },
}));
